<template>
  <div>
    <template v-if="profileLink">
      <router-link
        :to="{ name: 'PersonProfilePage', params: { personId: person.id } }"
        class="no-default-link-style"
        :target="linkTarget"
      >
        <slot name="display-name" v-bind="{ name: displayName }">
          {{ displayName }}
        </slot>
      </router-link>
    </template>
    <template v-else>
      <slot name="display-name" v-bind="{ name: displayName }">
        {{ displayName }}
      </slot>
    </template>
    <slot name="personNameAdditionalDetails" v-bind="{ person }" />
    <div class="muted--text">{{ displayDetails }}</div>
    <div v-if="$scopedSlots.personAdditionalDetails">
      <slot name="personAdditionalDetails" v-bind="{ person }" />
    </div>
  </div>
</template>
<script>
import gql from "fraql";
import { getFederationConfiguration } from "@composables/masterdata/config";
import { useMiscData } from "@composables/masterdata/misc";

export default {
  name: "AppShowPersonWithDetails",
  props: {
    person: {
      type: Object,
      required: true
    },
    paradressageGrade: {
      type: String,
      default: null
    },
    profileLink: {
      type: Boolean,
      default: false
    },
    linkTarget: {
      type: String,
      default: undefined
    },
    showIdentifier: {
      type: Boolean,
      default: false
    },
    detailsToShow: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  setup() {
    const { paradressageGradesMap } = useMiscData();
    return { ...getFederationConfiguration(), paradressageGradesMap };
  },
  computed: {
    displayName() {
      let personName = this.person.name;
      if (this.paradressageGrade) {
        personName += ` | ${
          this.paradressageGradesMap.find(i => i.id == this.paradressageGrade)
            ?.name
        }`;
      }

      const identifiers = this.showIdentifier
        ? [this.person.feiid, this.person.identifier].filter(Boolean)
        : [];

      return (
        personName + (identifiers.length ? ` (${identifiers.join(", ")})` : "")
      );
    },
    displayDetails() {
      const detailsToShow =
        this.detailsToShow.length == 0
          ? ["ageForSeason", "licenceCategoryName", "identifier", "clubName"]
          : this.detailsToShow;

      return detailsToShow
        .reduce((details, detail) => {
          switch (detail) {
            case "licenceCategoryName":
              details.push(this.person.licenceCategory?.name ?? "");
              break;
            case "identifier":
              details.push(
                this.person.identifier || this.person.identifierMasked
              );
              break;
            case "ageForSeason":
              if (this.person[detail]) details.push(this.person[detail] + "y.");
              break;
            case "clubName":
              if (this.person[detail]) {
                details.push(this.person[detail]);
              } else {
                details.push(this.person.country?.name ?? "");
              }
              break;
            default:
              details.push(this.person[detail] ?? "");
          }
          return details;
        }, [])
        .join(" / ");
    }
  },
  fragments: {
    person: gql`
      fragment _ on Person {
        id
        name
        firstName
        lastName
        identifierMasked
        identifier
        feiid
        clubName
        ageForSeason
        licenceCategoryId
        licenceCategory @client {
          id
          name
        }
        countryId
        country @client {
          id
          name
          alpha2
          ioc
        }
      }
    `
  }
};

export const REGISTRATION_PEOPLE_FRAGMENT = gql`
  fragment _ on RegistrationPerson {
    id
    personId
    registrationId
    personLicenceCategoryId
    personLicenceCategory @client {
      id
      name
    }
    personClubOrganisationId
    personClubOrganisation {
      id
      shortName
      name
    }
  }
`;

export function overwritePersonData({ people, registrationPeople }) {
  return people.map(p => {
    const overwrite = registrationPeople.find(rp => rp.personId == p.id);
    if (overwrite) {
      p.clubName =
        overwrite.personClubOrganisation?.shortName ||
        overwrite.personClubOrganisation?.name ||
        null;

      p.licenceCategoryId = overwrite.personLicenceCategoryId || null;
      p.licenceCategory = overwrite.personLicenceCategory || { name: null };
    }
    return p;
  });
}
</script>
